/**
 * Per-cell sightlines from one origin — the shared pass behind the armed attack wash and the
 * hover-intel weapon rings (`projects/range-preview-cover.md` F3). For each cell it answers what
 * {@link terrainVisibility} would for a Size-1 target standing there, but builds the object and
 * area contributions once per pass rather than once per cell, and drops cells off the board.
 */
import type { SightResult } from "@lancer-vtt/space";
import type { ReachCell } from "./attackRange";
import { areaContributions } from "./areaContributions";
import { isCharacter } from "./model/capabilities";
import { objectContributions, type ObjectContributions } from "./objectContributions";
import { foldTerrainVisibility } from "./terrain";
import type { Cell, CombatantId, EncounterState } from "./types";
import { worldFor } from "./world";

/** One on-board cell's sightline: {@link SightResult} keyed like its {@link ReachCell}. */
export type CellSight = SightResult & { key: string };

/** Named arguments for {@link sightlinesFrom}. */
export type SightlinesFromArgs = {
  state: EncounterState;
  /** The shooter's footprint cells. */
  from: Cell[];
  cells: readonly ReachCell[];
  /**
   * Extra hard-cover cell keys for a shot at `occupant` — the Goliath's Guardian
   * (`alliedCoverCells`, `@lancer-vtt/rules`), which this package cannot read. Called at most once
   * per occupant. Omitted means none.
   */
  extraHardCoverFor?: (occupant: CombatantId) => ReadonlySet<string>;
};

/**
 * The sightline to every on-board cell of `cells`, in order. A cell's occupant is excluded from
 * its own cover (`excludeCoverFrom`), choosing a character over an object on a shared cell as the
 * engine's `combatantAtCell` does. The object-cover sets rebuild only for a cell whose occupant
 * is itself a cover object, memoized per occupant.
 *
 * **Character-over-object preference.** The passable-object movement seam lets a character share
 * a cell with a passable object (a mech on a wreck, in smoke), and a cell-target attack resolves
 * against the character, making the object a third party whose cover counts. At most one live
 * character occupies a cell, so that pick is deterministic; an object occupant is the fallback,
 * first match winning an object-vs-object tie. Not a call to `combatantAtCell` itself, which is
 * square-only; this stays grid-agnostic.
 */
export function sightlinesFrom(args: SightlinesFromArgs): CellSight[] {
  const { state, from, cells, extraHardCoverFor } = args;
  const space = worldFor(state);
  const occupantByKey = new Map<string, CombatantId>();
  const objectFallbackByKey = new Map<string, CombatantId>();
  for (const c of Object.values(state.combatants)) {
    if (!c.pos) continue;
    for (const cell of space.footprint(c.pos, c.base.size).cells()) {
      const key = space.key(cell);
      if (isCharacter(c)) occupantByKey.set(key, c.id);
      else if (!objectFallbackByKey.has(key)) objectFallbackByKey.set(key, c.id);
    }
  }
  for (const [key, id] of objectFallbackByKey) {
    if (!occupantByKey.has(key)) occupantByKey.set(key, id);
  }

  const shared = objectContributions(space, state.combatants);
  const excluding = new Map<CombatantId, ObjectContributions>();
  const contribFor = (occupant: CombatantId | undefined): ObjectContributions => {
    const cover = occupant === undefined ? undefined : state.combatants[occupant]?.base.objectProfile?.cover;
    if (occupant === undefined || (cover !== "soft" && cover !== "hard")) return shared;
    let built = excluding.get(occupant);
    if (!built) {
      built = objectContributions(space, state.combatants, occupant);
      excluding.set(occupant, built);
    }
    return built;
  };
  const extraHard = new Map<CombatantId, ReadonlySet<string>>();
  const extraHardFor = (occupant: CombatantId | undefined): ReadonlySet<string> | undefined => {
    if (occupant === undefined || !extraHardCoverFor) return undefined;
    let built = extraHard.get(occupant);
    if (!built) {
      built = extraHardCoverFor(occupant);
      extraHard.set(occupant, built);
    }
    return built;
  };

  const areaContrib = state.areaEffects ? areaContributions(space, state.areaEffects, from) : undefined;
  const out: CellSight[] = [];
  for (const { key, cell } of cells) {
    if (!space.inBounds(cell)) continue;
    const occupant = occupantByKey.get(key);
    const sight = foldTerrainVisibility({
      space,
      terrain: state.terrain,
      from,
      to: [cell],
      edges: state.edges,
      contrib: contribFor(occupant),
      areaContrib,
      extraHardCoverCells: extraHardFor(occupant),
    });
    out.push({ key, visible: sight.visible, cover: sight.cover });
  }
  return out;
}
